import type { useStepper } from "../hooks/useStepper";
import { useStepResume } from "../hooks/useStepResume";
import ExecuteResumePeriodIcon from "../../../../assets/icons/execute-resume-period-icon.svg";
import ExecuteResumeEmployeeIcon from "../../../../assets/icons/execute-resume-employee-icon.svg";
import ExecuteResumeReportIcon from "../../../../assets/icons/execute-resume-report-icon.svg";

const StepResume = (props: ReturnType<typeof useStepper>) => {
  const { t } = props;
  const {
    selectedReports,
    selectedWorkers,
    startFormatted,
    endFormatted,
    workersContainerRef,
    workersListRef,
    reportsContainerRef,
    reportsListRef,
    visibleWorkersCount,
    isWorkersListTruncated,
    visibleReportsCount,
    isReportsListTruncated,
  } = useStepResume(props);

  return (
    <div className="w-full flex justify-center">
      <div
        style={{
          width: "100%",
          border: "1px solid #d9d9d9",
          borderRadius: "24px",
          padding: "clamp(18px, 3vw, 28px) clamp(18px, 4vw, 36px)",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          columnGap: "40px",
          rowGap: "24px",
          boxSizing: "border-box",
        }}
      >
        <section
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <div className="flex items-center gap-2">
            <img
              src={ExecuteResumeReportIcon}
              alt="Report Icon"
              style={{ width: "24px", height: "24px", flexShrink: 0 }}
            />
            <h6
              style={{
                margin: 0,
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "normal",
                color: "#4f4f4f",
              }}
            >
              {t("step4.info.report_type")}
            </h6>
          </div>

          <div
            ref={reportsContainerRef}
            style={{
              marginTop: "12px",
              maxHeight: "330px",
              overflowY: "hidden",
              paddingRight: "4px",
            }}
          >
            {selectedReports.length > 0 ? (
              <ul
                ref={reportsListRef}
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: "none",
                }}
              >
                {selectedReports.slice(0, visibleReportsCount).map((report) => (
                  <li
                    key={report}
                    style={{
                      margin: "0 0 10px",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "normal",
                      color: "#21171f",
                    }}
                  >
                    {report}
                  </li>
                ))}
                {isReportsListTruncated ? (
                  <li
                    style={{
                      margin: "0 0 10px",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "normal",
                      color: "#21171f",
                    }}
                  >
                    ...
                  </li>
                ) : null}
              </ul>
            ) : (
              <p
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "normal",
                  color: "#21171f",
                }}
              >
                {t("step4.info.empty_report")}
              </p>
            )}
          </div>
        </section>

        <section
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <div className="flex items-center gap-2">
            <img
              src={ExecuteResumeEmployeeIcon}
              alt="Employee Icon"
              style={{ width: "24px", height: "24px", flexShrink: 0 }}
            />
            <h6
              style={{
                margin: 0,
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "normal",
                color: "#4f4f4f",
              }}
            >
              {t("step4.info.employees")} ({selectedWorkers.length})
            </h6>
          </div>

          <div
            ref={workersContainerRef}
            style={{
              marginTop: "12px",
              maxHeight: "330px",
              overflowY: "hidden",
              paddingRight: "4px",
            }}
          >
            {selectedWorkers.length > 0 ? (
              <ul
                ref={workersListRef}
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: "none",
                }}
              >
                {selectedWorkers.slice(0, visibleWorkersCount).map((worker) => (
                  <li
                    key={worker.id}
                    style={{
                      margin: "0 0 10px",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "normal",
                      color: "#21171f",
                    }}
                  >
                    {worker.full_name}
                  </li>
                ))}
                {isWorkersListTruncated ? (
                  <li
                    style={{
                      margin: "0 0 10px",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "normal",
                      color: "#21171f",
                    }}
                  >
                    ...
                  </li>
                ) : null}
              </ul>
            ) : (
              <p
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "normal",
                  color: "#21171f",
                }}
              >
                {t("step4.info.empty_employees")}
              </p>
            )}
          </div>
        </section>

        <section
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <div className="flex items-center gap-2">
            <img
              src={ExecuteResumePeriodIcon}
              alt="Period Icon"
              style={{ width: "24px", height: "24px", flexShrink: 0 }}
            />
            <h6
              style={{
                margin: 0,
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "normal",
                color: "#4f4f4f",
              }}
            >
              {t("step4.info.period")}
            </h6>
          </div>

          <span
            style={{
              marginTop: "12px",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
            }}
          >
            {startFormatted} {endFormatted}
          </span>
        </section>
      </div>
    </div>
  );
};

export { StepResume };
